import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Search, 
  Filter, 
  CheckCircle, 
  XCircle, 
  ShieldAlert, 
  Eye, 
  FileText, 
  Award, 
  ArrowUpDown,
  MoreVertical,
  Check,
  Ban,
  RotateCcw
} from 'lucide-react';
import { StatusBadge } from '../components/common/StatusBadge';
import { Modal } from '../components/common/Modal';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { UserRecord, KYCSubmission, VendorTier } from '../types/admin';
import { AdminApiClient } from '../services/adminApiClient';

export const UsersView: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'directory' | 'kyc'>('directory');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState<'all' | 'buyer' | 'vendor'>('all');
  
  // Selected user for inspection drawer
  const [selectedUser, setSelectedUser] = useState<UserRecord | null>(null);
  
  // KYC Review state
  const [reviewingKyc, setReviewingKyc] = useState<KYCSubmission | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);

  // Moderation state
  const [userToBan, setUserToBan] = useState<UserRecord | null>(null);

  // Sample seed data reflecting real-time platform users
  const [users, setUsers] = useState<UserRecord[]>([
    {
      uid: 'usr_001_alex',
      name: 'Alex Okafor',
      email: 'alex.okafor@gmail.com',
      phone: '+234 803 123 4567',
      role: 'both',
      status: 'active',
      tier: 'tier2_silver',
      sellerTrustScore: 92,
      walletBalance: 48500,
      carbonOffsetKg: 84.5,
      tradesCompleted: 16,
      kycStatus: 'verified',
    },
    {
      uid: 'usr_002_tunde',
      name: 'Tunde Bakare',
      email: 'tunde.bakare@yahoo.com',
      phone: '+234 802 987 6543',
      role: 'vendor',
      status: 'active',
      tier: 'tier3_champion',
      sellerTrustScore: 98,
      walletBalance: 142000,
      carbonOffsetKg: 312.0,
      tradesCompleted: 58,
      kycStatus: 'verified',
    },
    {
      uid: 'usr_003_chioma',
      name: 'Chioma Eze',
      email: 'chioma.eze@hotmail.com',
      phone: '+234 814 555 1212',
      role: 'buyer',
      status: 'active',
      tier: 'tier1_bronze',
      sellerTrustScore: 80,
      walletBalance: 12000,
      carbonOffsetKg: 18.2,
      tradesCompleted: 4,
      kycStatus: 'unverified',
    },
    {
      uid: 'usr_004_fatima',
      name: 'Fatima Sani',
      email: 'fatima.s@outlook.com',
      phone: '+234 809 333 4444',
      role: 'vendor',
      status: 'active',
      tier: 'tier1_bronze',
      sellerTrustScore: 75,
      walletBalance: 6500,
      carbonOffsetKg: 12.0,
      tradesCompleted: 2,
      kycStatus: 'pending',
    },
    {
      uid: 'usr_005_emeka',
      name: 'Emeka Johnson',
      email: 'emeka.j@proton.me',
      phone: '+234 701 888 9999',
      role: 'vendor',
      status: 'suspended',
      tier: 'tier1_bronze',
      sellerTrustScore: 42,
      walletBalance: 0,
      carbonOffsetKg: 5.0,
      tradesCompleted: 1,
      kycStatus: 'rejected',
    },
  ]);

  const [kycQueue, setKycQueue] = useState<KYCSubmission[]>([
    {
      id: 'kyc_sub_101',
      userId: 'usr_004_fatima',
      userName: 'Fatima Sani',
      userEmail: 'fatima.s@outlook.com',
      docType: 'nin',
      docNumber: '5482 9102 3841',
      documentUrl: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=600&auto=format&fit=crop&q=80',
      bankName: 'Guaranty Trust Bank (GTBank)',
      accountNumber: '0123456789',
      accountName: 'Fatima Sani',
      residentialAddress: '12 Admiralty Way, Lekki Phase 1, Lagos',
      submittedAt: 'Today at 09:30 AM',
      status: 'pending',
    },
    {
      id: 'kyc_sub_102',
      userId: 'usr_006_kemi',
      userName: 'Kemi Adeleke',
      userEmail: 'kemi.adeleke@gmail.com',
      docType: 'drivers_license',
      docNumber: 'FRSC-LA-294021',
      documentUrl: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=600&auto=format&fit=crop&q=80',
      bankName: 'Access Bank',
      accountNumber: '0987654321',
      accountName: 'Kemi Adeleke',
      residentialAddress: '4 Allen Avenue, Ikeja, Lagos',
      submittedAt: 'Yesterday at 04:15 PM',
      status: 'pending',
    },
    {
      id: 'kyc_sub_103',
      userId: 'usr_007_chidi',
      userName: 'Chidi Nnamdi',
      userEmail: 'chidi.ecofurnish@gmail.com',
      docType: 'cac',
      docNumber: 'RC-1849204',
      businessName: 'EcoFurnishings Nigeria Ltd',
      documentUrl: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=600&auto=format&fit=crop&q=80',
      bankName: 'Zenith Bank',
      accountNumber: '2018492040',
      accountName: 'EcoFurnishings Nigeria Ltd',
      residentialAddress: '88 Commercial Ave, Yaba, Lagos',
      submittedAt: '2 days ago',
      status: 'pending',
    },
  ]);

  useEffect(() => {
    AdminApiClient.getUsers()
      .then((data) => {
        if (data && data.length > 0) {
          const mapped: UserRecord[] = data.map((u) => ({
            uid: u.id,
            name: u.displayName || u.legalName || 'User',
            email: u.email,
            phone: u.phoneNumber || 'N/A',
            role: u.role === 'VERIFIED_SELLER' ? 'vendor' : u.role === 'BUYER' ? 'buyer' : 'both',
            status: u.role === 'BANNED' ? 'banned' : 'active',
            tier: u.role === 'VERIFIED_SELLER' ? 'tier2_silver' : 'tier1_bronze',
            sellerTrustScore: u.kycStatus === 'VERIFIED' ? 95 : 75,
            walletBalance: parseFloat(u.walletBalance || '0'),
            carbonOffsetKg: parseFloat(u.carbonOffsetKg || '0'),
            tradesCompleted: (u._count?.boughtOrders || 0) + (u._count?.soldOrders || 0),
            kycStatus: u.kycStatus === 'VERIFIED' ? 'verified' : u.kycStatus === 'PENDING' ? 'pending' : 'unverified',
          }));
          setUsers(mapped);
        }
      })
      .catch((err) => console.error('Failed to load users from API:', err));
  }, []);

  const handleApproveKyc = async (kyc: KYCSubmission) => {
    try {
      await AdminApiClient.updateKyc(kyc.userId, 'VERIFIED');
    } catch (e) {
      console.error('Failed to approve KYC:', e);
    }
    setKycQueue(prev => prev.filter(k => k.id !== kyc.id));
    setUsers(prev => prev.map(u => {
      if (u.uid === kyc.userId) {
        return {
          ...u,
          tier: 'tier2_silver',
          kycStatus: 'verified',
          sellerTrustScore: Math.min(100, u.sellerTrustScore + 20)
        };
      }
      return u;
    }));
    setReviewingKyc(null);
  };

  const handleRejectKyc = async () => {
    if (!reviewingKyc) return;
    try {
      await AdminApiClient.updateKyc(reviewingKyc.userId, 'REJECTED');
    } catch (e) {
      console.error('Failed to reject KYC:', e);
    }
    setKycQueue(prev => prev.filter(k => k.id !== reviewingKyc.id));
    setUsers(prev => prev.map(u => {
      if (u.uid === reviewingKyc.userId) {
        return { ...u, kycStatus: 'rejected' };
      }
      return u;
    }));
    setShowRejectModal(false);
    setReviewingKyc(null);
    setRejectReason('');
  };

  const handleToggleSuspend = async (uid: string) => {
    const target = users.find(u => u.uid === uid);
    const nextStatus = target?.status === 'suspended' ? 'active' : 'suspended';
    try {
      await AdminApiClient.updateUser(uid, { role: nextStatus === 'suspended' ? 'SUSPENDED' : 'BUYER' });
    } catch (e) {
      console.error('Failed to toggle suspend:', e);
    }
    setUsers(prev => prev.map(u => {
      if (u.uid === uid) {
        return { ...u, status: nextStatus };
      }
      return u;
    }));
  };

  const handleConfirmBan = async () => {
    if (!userToBan) return;
    try {
      await AdminApiClient.updateUser(userToBan.uid, { role: 'BANNED' });
    } catch (e) {
      console.error('Failed to ban user:', e);
    }
    setUsers(prev => prev.map(u => {
      if (u.uid === userToBan.uid) {
        return { ...u, status: 'banned' };
      }
      return u;
    }));
    setUserToBan(null);
  };

  const filteredUsers = users.filter(user => {
    const matchSearch = user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        user.phone?.includes(searchQuery);
    const matchRole = filterRole === 'all' || user.role === filterRole || user.role === 'both';
    return matchSearch && matchRole;
  });

  return (
    <div className="space-y-6">
      {/* Tab Switcher */}
      <div className="flex items-center justify-between border-b border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC] pb-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setActiveTab('directory')}
            className={`px-5 py-2.5 rounded-full text-xs font-bold transition-all
              ${activeTab === 'directory'
                ? 'bg-brand-500 text-forest-void shadow-glow-sm'
                : 'text-[#90cbb7] hover:text-white dark:text-[#90cbb7] dark:hover:text-white light:text-[#527063] light:hover:text-[#0D1C17]'
              }
            `}
          >
            User Directory ({users.length})
          </button>

          <button
            onClick={() => setActiveTab('kyc')}
            className={`relative px-5 py-2.5 rounded-full text-xs font-bold transition-all
              ${activeTab === 'kyc'
                ? 'bg-brand-500 text-forest-void shadow-glow-sm'
                : 'text-[#90cbb7] hover:text-white dark:text-[#90cbb7] dark:hover:text-white light:text-[#527063] light:hover:text-[#0D1C17]'
              }
            `}
          >
            <span>KYC Verification Queue</span>
            {kycQueue.length > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] bg-amber-500 text-forest-void font-extrabold">
                {kycQueue.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {activeTab === 'directory' ? (
        /* USER DIRECTORY TAB */
        <div className="space-y-4">
          {/* Filters Bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 rounded-2xl border
            bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
            light:bg-white light:border-[#D5E4DC]
          ">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Filter by name, email, phone..."
                className="w-full pl-9 pr-4 py-2 rounded-xl text-xs outline-none border transition-colors
                  bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                  light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                  focus:border-brand-500"
              />
            </div>

            <div className="flex items-center gap-2 self-end sm:self-auto">
              <span className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">Role:</span>
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value as any)}
                className="px-3 py-1.5 rounded-xl text-xs font-medium border outline-none
                  bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                  light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                "
              >
                <option value="all">All Roles</option>
                <option value="vendor">Vendors Only</option>
                <option value="buyer">Buyers Only</option>
              </select>
            </div>
          </div>

          {/* User Table */}
          <div className="rounded-3xl border overflow-hidden transition-colors
            bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
            light:bg-white light:border-[#D5E4DC] light:shadow-sm
          ">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="border-b border-[#22493c] dark:border-[#22493c] light:border-[#EBF2EE] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] uppercase tracking-wider font-semibold">
                    <th className="py-4 px-6">User / Merchant</th>
                    <th className="py-4 px-6">Tier & Badge</th>
                    <th className="py-4 px-6">Trust Score</th>
                    <th className="py-4 px-6">Wallet Balance</th>
                    <th className="py-4 px-6">CO2 Saved</th>
                    <th className="py-4 px-6">Status</th>
                    <th className="py-4 px-6 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#22493c] dark:divide-[#22493c] light:divide-[#EBF2EE]">
                  {filteredUsers.map((user) => (
                    <tr key={user.uid} className="hover:bg-[#1a382e]/50 dark:hover:bg-[#1a382e]/50 light:hover:bg-gray-50/80 transition-colors">
                      <td className="py-4 px-6">
                        <div className="font-bold text-sm text-white dark:text-white light:text-[#0D1C17]">
                          {user.name}
                        </div>
                        <div className="text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                          {user.email} • {user.phone}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <StatusBadge status={user.tier} />
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <span className={`font-bold ${user.sellerTrustScore > 85 ? 'text-brand-400' : user.sellerTrustScore > 60 ? 'text-amber-400' : 'text-rose-400'}`}>
                            {user.sellerTrustScore} / 100
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6 font-bold text-white dark:text-white light:text-[#0D1C17]">
                        ₦{user.walletBalance.toLocaleString()}
                      </td>
                      <td className="py-4 px-6 text-brand-400 font-semibold">
                        {user.carbonOffsetKg} kg
                      </td>
                      <td className="py-4 px-6">
                        <StatusBadge status={user.status} />
                      </td>
                      <td className="py-4 px-6 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setSelectedUser(user)}
                            title="View Full User Dossier"
                            className="p-1.5 rounded-lg text-[#90cbb7] hover:text-white hover:bg-[#22493c] light:hover:bg-gray-100"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleSuspend(user.uid)}
                            title={user.status === 'suspended' ? 'Reactivate' : 'Suspend Account'}
                            className={`p-1.5 rounded-lg ${user.status === 'suspended' ? 'text-emerald-400 hover:bg-emerald-500/15' : 'text-amber-400 hover:bg-amber-500/15'}`}
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                          {user.status !== 'banned' && (
                            <button
                              onClick={() => setUserToBan(user)}
                              title="Ban User"
                              className="p-1.5 rounded-lg text-rose-400 hover:bg-rose-500/15"
                            >
                              <Ban className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        /* KYC VERIFICATION QUEUE TAB */
        <div className="space-y-6">
          <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs flex items-center gap-3">
            <ShieldAlert className="w-5 h-5 shrink-0" />
            <span>
              <strong>Tier 2 Silver Merchant Verification:</strong> Review government issued identification (NIN, Driver's License, International Passport, Voter's Card, or CAC Business Registration). Ensure bank account details match CAC company name or personal legal ID before approving.
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {kycQueue.map((kyc) => (
              <div 
                key={kyc.id} 
                className="rounded-3xl p-6 border transition-all flex flex-col justify-between
                  bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
                  light:bg-white light:border-[#D5E4DC] light:shadow-sm
                "
              >
                <div>
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="text-[10px] font-extrabold px-2.5 py-0.5 rounded-full bg-brand-500/15 text-brand-400 border border-brand-500/30 uppercase tracking-wider">
                        {kyc.docType === 'cac' ? 'CAC Business' : kyc.docType.replace('_', ' ').toUpperCase()}
                      </span>
                      <h4 className="mt-2 text-base font-bold text-white dark:text-white light:text-[#0D1C17]">
                        {kyc.userName}
                      </h4>
                      <p className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                        {kyc.userEmail}
                      </p>
                      {kyc.businessName && (
                        <p className="text-xs font-semibold text-amber-400 mt-1">
                          🏢 {kyc.businessName}
                        </p>
                      )}
                    </div>
                    <span className="text-[11px] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                      {kyc.submittedAt}
                    </span>
                  </div>

                  <div className="mt-4 p-3 rounded-xl bg-[#10231d] dark:bg-[#10231d] light:bg-gray-50 border border-[#22493c] dark:border-[#22493c] light:border-gray-200">
                    <p className="text-[11px] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                      Document ID / RC Number:
                    </p>
                    <p className="font-mono text-xs font-bold text-white dark:text-white light:text-[#0D1C17] mt-0.5">
                      {kyc.docNumber}
                    </p>
                  </div>

                  {kyc.bankName && (
                    <div className="mt-3 p-3 rounded-xl bg-[#10231d] dark:bg-[#10231d] light:bg-gray-50 border border-[#22493c] dark:border-[#22493c] light:border-gray-200">
                      <p className="text-[11px] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                        Payout Bank Account:
                      </p>
                      <p className="text-xs font-bold text-white dark:text-white light:text-[#0D1C17] mt-0.5">
                        {kyc.bankName} • {kyc.accountNumber}
                      </p>
                      <p className="text-[11px] text-brand-400 font-medium mt-0.5">
                        Holder: {kyc.accountName}
                      </p>
                    </div>
                  )}

                  {/* Dynamic Rule Match Verification Box */}
                  <div className={`mt-3 p-2.5 rounded-xl border text-[11px] ${
                    kyc.docType === 'cac'
                      ? 'bg-amber-500/10 border-amber-500/30 text-amber-300'
                      : 'bg-blue-500/10 border-blue-500/30 text-blue-300'
                  }`}>
                    {kyc.docType === 'cac' ? (
                      <span><strong>CAC Rule:</strong> Payout name must match registered company <em>"{kyc.businessName}"</em></span>
                    ) : (
                      <span><strong>Personal ID Rule:</strong> Payout name must match applicant name <em>"{kyc.userName}"</em></span>
                    )}
                  </div>

                  {kyc.documentUrl && (
                    <div className="mt-4">
                      <p className="text-[11px] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                        Uploaded Document Proof:
                      </p>
                      <div 
                        onClick={() => setReviewingKyc(kyc)}
                        className="cursor-pointer group relative h-36 rounded-2xl overflow-hidden border border-[#22493c] dark:border-[#22493c]"
                      >
                        <img 
                          src={kyc.documentUrl} 
                          alt="KYC Document" 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform" 
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs font-bold gap-1.5">
                          <Eye className="w-4 h-4" />
                          <span>Inspect Full Slip</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex items-center gap-3 pt-4 border-t border-[#22493c] dark:border-[#22493c] light:border-[#EBF2EE]">
                  <button
                    onClick={() => {
                      setReviewingKyc(kyc);
                      setShowRejectModal(true);
                    }}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-xs font-bold text-rose-400 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/20 transition-colors"
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    <span>Reject</span>
                  </button>

                  <button
                    onClick={() => handleApproveKyc(kyc)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-xs font-bold text-forest-void bg-brand-500 hover:bg-brand-400 shadow-glow-sm transition-all"
                  >
                    <CheckCircle className="w-3.5 h-3.5" />
                    <span>Approve</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KYC Inspect & Reject Dialog */}
      {reviewingKyc && showRejectModal && (
        <Modal 
          isOpen={showRejectModal} 
          onClose={() => setShowRejectModal(false)}
          title={`Reject KYC: ${reviewingKyc.userName}`}
          subtitle="Provide a clear reason that will be delivered directly to the user's notification tray"
          maxWidth="md"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                Rejection Reason:
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g. Document image is blurred, name does not match bank account, or invalid NIN format."
                rows={3}
                className="w-full p-3 rounded-2xl text-xs outline-none border transition-colors
                  bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                  light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                  focus:border-rose-500"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-[#22493c] dark:border-[#22493c]">
              <button
                onClick={() => setShowRejectModal(false)}
                className="px-4 py-2 rounded-full text-xs font-bold text-white bg-[#22493c] hover:bg-[#2a5547]"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectKyc}
                disabled={!rejectReason.trim()}
                className="px-5 py-2 rounded-full text-xs font-bold text-white bg-rose-600 hover:bg-rose-500 disabled:opacity-50"
              >
                Confirm Rejection
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* User Inspection Modal Drawer */}
      {selectedUser && (
        <Modal
          isOpen={Boolean(selectedUser)}
          onClose={() => setSelectedUser(null)}
          title={`User Dossier: ${selectedUser.name}`}
          subtitle={`User UID: ${selectedUser.uid}`}
          maxWidth="lg"
        >
          <div className="space-y-6 text-xs">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="p-3 rounded-2xl bg-[#10231d] dark:bg-[#10231d] light:bg-gray-50 border border-[#22493c] dark:border-[#22493c]">
                <p className="text-[#90cbb7] light:text-gray-500">Wallet</p>
                <p className="text-base font-bold text-white dark:text-white light:text-gray-900 mt-1">
                  ₦{selectedUser.walletBalance.toLocaleString()}
                </p>
              </div>

              <div className="p-3 rounded-2xl bg-[#10231d] dark:bg-[#10231d] light:bg-gray-50 border border-[#22493c] dark:border-[#22493c]">
                <p className="text-[#90cbb7] light:text-gray-500">Trust Score</p>
                <p className="text-base font-bold text-brand-400 mt-1">
                  {selectedUser.sellerTrustScore}/100
                </p>
              </div>

              <div className="p-3 rounded-2xl bg-[#10231d] dark:bg-[#10231d] light:bg-gray-50 border border-[#22493c] dark:border-[#22493c]">
                <p className="text-[#90cbb7] light:text-gray-500">Trades Done</p>
                <p className="text-base font-bold text-white dark:text-white light:text-gray-900 mt-1">
                  {selectedUser.tradesCompleted}
                </p>
              </div>

              <div className="p-3 rounded-2xl bg-[#10231d] dark:bg-[#10231d] light:bg-gray-50 border border-[#22493c] dark:border-[#22493c]">
                <p className="text-[#90cbb7] light:text-gray-500">CO2 Diverted</p>
                <p className="text-base font-bold text-brand-400 mt-1">
                  {selectedUser.carbonOffsetKg} kg
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <h5 className="font-bold text-white dark:text-white light:text-gray-900">Contact & Role</h5>
              <p className="text-[#90cbb7] light:text-gray-600">Email: {selectedUser.email}</p>
              <p className="text-[#90cbb7] light:text-gray-600">Phone: {selectedUser.phone || 'N/A'}</p>
              <p className="text-[#90cbb7] light:text-gray-600">Account Type: {selectedUser.role.toUpperCase()}</p>
              <p className="text-[#90cbb7] light:text-gray-600">Verification Tier: {selectedUser.tier}</p>
            </div>
          </div>
        </Modal>
      )}

      {/* Ban Confirmation Dialog */}
      {userToBan && (
        <ConfirmDialog
          isOpen={Boolean(userToBan)}
          onClose={() => setUserToBan(null)}
          onConfirm={handleConfirmBan}
          title={`Ban User Account: ${userToBan.name}?`}
          description={`Banning this account will immediately revoke authentication, cancel active listings, and prevent any wallet transactions. Are you sure you want to proceed?`}
          confirmButtonText="Ban Account"
          variant="danger"
        />
      )}
    </div>
  );
};
